"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import { useRouter } from "next/navigation";
import type {
  CPollsContent,
  CPollsListContent,
  CPollsListItem,
  CPollsPollContent,
  CPollsCompletedContent,
  CPollsScoreContent,
  CPollQuestion,
  CPollScoreQuestion,
} from "@/lib/api/cms-catalog";
import {
  savePoll,
  updateFillTime,
  validatePollEmail,
  type CPollVoteValue,
} from "@/lib/api/c-polls";
import type { FrontendUserSession } from "@/lib/api/session";
import { useT } from "@/lib/i18n/context";
import { Field, Input, Textarea, Radio, Checkbox, FormStatus, SubmitButton } from "@/components/ui/Form";
import { cPollsTranslations, type CPollsTranslations } from "./CPolls.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CPolls.module.css";

/* Wykres wyników (Apache ECharts) - client-only (canvas wymaga window). */
const PollScoreChart = dynamic(
  () => import("./PollScoreChart").then((m) => m.PollScoreChart),
  { ssr: false },
);

/**
 * CPolls - visitor view komponentu `c_polls` (Interaktywne formularze / ankiety).
 *
 * **Data source:** `C_POLLS_CONTROLLER::getApiContent()` zwraca jeden z czterech
 * widoków (discriminator `content.view`):
 *   - `list`      - paginowana lista ankiet (rozmiar strony z config `per_page`)
 *   - `poll`      - wypełnianie ankiety (formularz, 5 typów pytań, limit czasu,
 *                   stronicowanie, dane gościa)
 *   - `completed` - ekran "dziękujemy" + link do wyników
 *   - `score`     - wyniki z wykresami ECharts
 *
 * Wybór widoku jest server-driven (pageBootstrap re-fetch przy nawigacji), a
 * formularz wypełniania jest client-interactive (POST przez proxy + nawigacja).
 */
export function CPolls({ content, userSession = null }: CPollsProps) {
  const t = useT(cPollsTranslations);

  if (!content || typeof content !== "object") {
    return <div className={styles.empty}>{t.emptyData}</div>;
  }

  /* Backend `getApiContent` jest wołany ANONIMOWO przez pageBootstrap (sesja
     frontendu NIE jest forwardowana - tak jak w c_user_accounts), więc
     `guest.required` z backendu jest zawsze `true`. Stan logowania znamy z
     SSR-injected `userSession` - zalogowany user NIE widzi pól gościa (jego
     głos i tak przypisze się po zalogowanym Bearer w endpoint'cie save). */
  const isLoggedIn = userSession !== null;

  switch (content.view) {
    case "poll":
      /* Key per krok stronicowania - remount czystego formularza po przejściu
         do kolejnej partii pytań (router.refresh zmienia filled_question). */
      return (
        <PollView
          key={content.progress.filled_question}
          content={content}
          isLoggedIn={isLoggedIn}
          t={t}
        />
      );
    case "completed":
      return <CompletedView content={content} t={t} />;
    case "score":
      return <ScoreView content={content} t={t} />;
    case "list":
    default:
      return <ListView content={content as CPollsListContent} t={t} />;
  }
}

export type CPollsProps = {
  content: CPollsContent;
  /** SSR-resolved sesja użytkownika frontendu (null = gość). Override dla
      `guest.required` (backend anonimowy zawsze raportuje `true`). */
  userSession?: FrontendUserSession | null;
};

/* ─── List view ───────────────────────────────────────────────────────────── */

function ListView({
  content,
  t,
}: {
  content: CPollsListContent;
  t: CPollsTranslations;
}) {
  const items: CPollsListItem[] = Array.isArray(content.items) ? content.items : [];

  if (items.length === 0) {
    return <div className={styles.empty}>{t.list.empty}</div>;
  }

  return (
    <ul className={styles.list}>
      {items.map((item) => {
        const fillHref = extractInternalHref(item.href.art);
        const scoreHref = item.href.score ? extractInternalHref(item.href.score) : null;
        return (
          <li key={item.id} className={styles.listItem}>
            <div className={styles.listItemBody}>
              <h2 className={styles.listItemTitle}>{item.name}</h2>
              {item.text && (
                <div
                  className={styles.listItemText}
                  dangerouslySetInnerHTML={{ __html: item.text }}
                />
              )}
              {item.was_filled && (
                <span className={styles.badgeFilled}>{t.list.alreadyFilled}</span>
              )}
            </div>
            <div className={styles.listItemActions}>
              <LinkButton href={fillHref} fallback={item.href.art}>
                {t.list.fillOut}
              </LinkButton>
              {scoreHref !== null && (
                <LinkButton href={scoreHref} fallback={item.href.score!} variant="secondary">
                  {t.list.showResults}
                </LinkButton>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

/* ─── Poll fill view ──────────────────────────────────────────────────────── */

type VotesState = Record<number, string | string[]>;
type ErrorsState = {
  email?: string;
  name?: string;
  surname?: string;
  telephone?: string;
  votes?: Record<number, string>;
  submit?: string;
};

function PollView({
  content,
  isLoggedIn,
  t,
}: {
  content: CPollsPollContent;
  isLoggedIn: boolean;
  t: CPollsTranslations;
}) {
  const router = useRouter();
  const backHref = extractInternalHref(content.list_href);

  /* Ankieta już wypełniona - komunikat zamiast formularza (parity z legacy). */
  if (content.is_filled) {
    return (
      <div className={styles.poll}>
        {content.poll.text && (
          <div
            className={styles.pollText}
            dangerouslySetInnerHTML={{ __html: content.poll.text }}
          />
        )}
        <FormStatus variant="warning">
          {t.poll.alreadyFilled}
          {content.next_fill_at && (
            <>
              <br />
              {t.poll.nextFill(t.formatNextFill(content.next_fill_at))}
            </>
          )}
        </FormStatus>
        <div className={styles.pollActions}>
          <LinkButton href={backHref} fallback={content.list_href} variant="secondary">
            {t.poll.back}
          </LinkButton>
        </div>
      </div>
    );
  }

  return (
    <PollForm
      content={content}
      isLoggedIn={isLoggedIn}
      t={t}
      router={router}
      backHref={backHref}
    />
  );
}

function PollForm({
  content,
  isLoggedIn,
  t,
  router,
  backHref,
}: {
  content: CPollsPollContent;
  isLoggedIn: boolean;
  t: CPollsTranslations;
  router: ReturnType<typeof useRouter>;
  backHref: string | null;
}) {
  const { poll } = content;

  /* Efektywna wymagalność pól gościa: backend anonimowy raportuje zawsze
     `true`, ale zalogowany user (SSR `userSession`) pól nie wypełnia. */
  const guestRequired = content.guest.required && !isLoggedIn;

  /* Pytania + postęp w stanie - aktualizowane WPROST z odpowiedzi `next_step`
     przy kolejnych krokach stronicowania (BEZ re-fetcha pageBootstrap, który
     jest anonimowy + cache 60s i nie widziałby głosów bieżącego usera). */
  const [questions, setQuestions] = useState<CPollQuestion[]>(content.questions);
  const [progress, setProgress] = useState(content.progress);

  /* Stan głosów - inicjalizacja per typ pytania. */
  const [votes, setVotes] = useState<VotesState>(() => buildInitialVotes(content.questions));
  const [justifications, setJustifications] = useState<Record<number, string>>({});
  const [guestData, setGuestData] = useState({ email: "", name: "", surname: "", telephone: "" });
  const [errors, setErrors] = useState<ErrorsState>({});
  const [submitting, setSubmitting] = useState(false);
  const [remain, setRemain] = useState<number | null>(poll.remain_time);

  /* Refs dla logiki czasu / wypełnienia używanej w interwałach. `lastSyncRef`
     inicjalizowany 0 (Date.now() w render-phase = impure) i ustawiany na mount. */
  const filledIdRef = useRef<number | null>(content.last_filled_id);
  const lastSyncRef = useRef<number>(0);
  const finishingRef = useRef(false);
  const submitRef = useRef<(ignore: boolean) => void>(() => {});

  /* Czy bieżący krok jest ostatni (label przycisku: zakończ vs dalej). */
  const isLastStep = progress.filled_question + questions.length >= progress.question_count;

  const setVote = useCallback((qid: number, value: string | string[]) => {
    setVotes((prev) => ({ ...prev, [qid]: value }));
  }, []);

  const handleSubmit = useCallback(
    async (ignore: boolean) => {
      if (submitting || finishingRef.current) return;

      /* Elapsed (sekundy od ostatniej synchronizacji) -> akumulacja fill_time. */
      const now = Date.now();
      const base = lastSyncRef.current || now;
      const elapsed = Math.max(0, Math.round((now - base) / 1000));
      lastSyncRef.current = now;

      /* Walidacja client-side (pomijana przy wymuszonym zakończeniu/ignore). */
      if (!ignore) {
        const nextErrors = validateForm(questions, votes, guestRequired, guestData, t);
        if (hasErrors(nextErrors)) {
          setErrors(nextErrors);
          return;
        }
      }

      setErrors({});
      setSubmitting(true);
      if (ignore) finishingRef.current = true;

      const payloadVotes = buildPayloadVotes(questions, votes);
      const payloadJustifications = buildPayloadJustifications(questions, votes, justifications);

      try {
        const result = await savePoll({
          id_poll: poll.id,
          id_catalog: content.id,
          votes: payloadVotes,
          justifications: payloadJustifications,
          ...(guestRequired ? guestData : {}),
          filled_poll_id: filledIdRef.current,
          ignore,
          elapsed,
        });

        if (result.ok) {
          if (result.done) {
            if (result.completed_href) {
              const internal = extractInternalHref(result.completed_href);
              router.push(internal ?? result.completed_href);
            } else {
              router.refresh();
            }
          } else {
            /* Kolejny krok - aplikujemy `next_step` WPROST (bez re-fetcha). */
            filledIdRef.current = result.filled_poll_id;
            if (result.next_step) {
              setQuestions(result.next_step.questions);
              setProgress(result.next_step.progress);
              setVotes(buildInitialVotes(result.next_step.questions));
              setJustifications({});
              setErrors({});
              setSubmitting(false);
              finishingRef.current = false;
              lastSyncRef.current = Date.now();
              if (typeof window !== "undefined") {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            } else {
              router.refresh();
            }
          }
        } else {
          setSubmitting(false);
          finishingRef.current = false;
          setErrors(mapBackendErrors(result.errors, result.message, t));
        }
      } catch {
        setSubmitting(false);
        finishingRef.current = false;
        setErrors({ submit: t.poll.errors.submit });
      }
    },
    [submitting, questions, votes, justifications, guestRequired, guestData, poll.id, content.id, router, t],
  );

  /* Trzymamy aktualny handler w ref - interwały (czas) wywołują najnowszą wersję. */
  useEffect(() => {
    submitRef.current = handleSubmit;
  }, [handleSubmit]);

  /* Punkt startu pomiaru czasu (Date.now() poza render-phase). */
  useEffect(() => {
    lastSyncRef.current = Date.now();
  }, []);

  /* Odliczanie (gdy ankieta ma limit czasu). Co sekundę dekrementacja; po
     osiągnięciu 0 wymuszamy zapis z `ignore` (finalizacja częściowa). */
  useEffect(() => {
    if (remain === null) return;
    const id = setInterval(() => {
      setRemain((r) => {
        if (r === null) return r;
        const next = r - 1;
        if (next <= 0) {
          clearInterval(id);
          submitRef.current(true);
          return 0;
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(id);
    /* Tylko on-mount - dekrementacja przez setRemain functional update. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* Synchronizacja czasu z backendem co 15s - persystuje fill_time i autorytatywnie
     zwraca pozostały czas + flagę końca (gdy serwer wykryje koniec wcześniej). */
  useEffect(() => {
    if (poll.fill_time <= 0) return;
    const id = setInterval(async () => {
      if (finishingRef.current) return;
      /* Bez `filled_poll_id` backend nie ma rekordu do akumulacji - zwracałby
         `remain_time = fill_time*60 - elapsed_delta` co cofa odliczanie w UI
         (np. 30s -> 45s co 15s pingu). Czekamy na pierwszy `savePoll`, który
         utworzy rekord i wypełni `filledIdRef`. */
      if (!filledIdRef.current) return;
      const now = Date.now();
      const base = lastSyncRef.current || now;
      const elapsed = Math.max(0, Math.round((now - base) / 1000));
      lastSyncRef.current = now;
      try {
        const res = await updateFillTime(poll.id, filledIdRef.current, elapsed);
        if (res.remain_time !== null) setRemain(res.remain_time);
        if (res.end_time) submitRef.current(true);
      } catch {
        /* Ping nieudany - odliczanie client-side i tak wymusi finalizację. */
      }
    }, 15000);
    return () => clearInterval(id);
  }, [poll.id, poll.fill_time]);

  /* Live postęp: doliczamy pytania bieżącego kroku które JUŻ mają odpowiedź
     (recompute co render -> aktualizuje % na bieżąco przy zaznaczaniu). */
  const answeredNow = questions.reduce(
    (n, q) => (isAnswered(q, votes[q.id]) ? n + 1 : n),
    0,
  );
  const filledTotal = progress.filled_question + answeredNow;
  const remainingQuestions = Math.max(0, progress.question_count - filledTotal);
  const percentDone = progress.question_count > 0
    ? Math.round((filledTotal / progress.question_count) * 100)
    : 0;

  return (
    <form
      className={styles.poll}
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        handleSubmit(false);
      }}
    >
      {poll.text && (
        <div
          className={styles.pollText}
          dangerouslySetInnerHTML={{ __html: poll.text }}
        />
      )}

      {/* Dane gościa (niezalogowany). */}
      {guestRequired && (
        <fieldset className={styles.guestFieldset}>
          <legend className={styles.guestLegend}>{t.poll.guestHeading}</legend>
          <div className={styles.guestGrid}>
            <Field label={t.poll.labels.email} required error={errors.email}>
              <Input
                type="email"
                autoComplete="email"
                value={guestData.email}
                onChange={(e) => setGuestData((d) => ({ ...d, email: e.target.value }))}
                onBlur={async (e) => {
                  const email = e.target.value.trim();
                  if (email && /.+@.+\..+/.test(email)) {
                    const available = await validatePollEmail(email);
                    if (!available)
                      setErrors((prev) => ({ ...prev, email: t.poll.errors.emailTaken }));
                  }
                }}
              />
            </Field>
            <Field label={t.poll.labels.name} required error={errors.name}>
              <Input
                type="text"
                autoComplete="given-name"
                value={guestData.name}
                onChange={(e) => setGuestData((d) => ({ ...d, name: e.target.value }))}
              />
            </Field>
            <Field label={t.poll.labels.surname} required error={errors.surname}>
              <Input
                type="text"
                autoComplete="family-name"
                value={guestData.surname}
                onChange={(e) => setGuestData((d) => ({ ...d, surname: e.target.value }))}
              />
            </Field>
            <Field label={t.poll.labels.telephone} required error={errors.telephone}>
              <Input
                type="tel"
                autoComplete="tel"
                value={guestData.telephone}
                onChange={(e) => setGuestData((d) => ({ ...d, telephone: e.target.value }))}
              />
            </Field>
          </div>
        </fieldset>
      )}

      {/* Pytania bieżącego kroku. */}
      <ol className={styles.questions} start={progress.filled_question + 1}>
        {questions.map((q, idx) => (
          <li key={q.id} className={styles.question}>
            <QuestionField
              question={q}
              number={progress.filled_question + idx + 1}
              value={votes[q.id]}
              justification={justifications[q.id] ?? ""}
              error={errors.votes?.[q.id]}
              onChange={(v) => setVote(q.id, v)}
              onJustificationChange={(text) =>
                setJustifications((prev) => ({ ...prev, [q.id]: text }))
              }
              t={t}
            />
          </li>
        ))}
      </ol>

      {/* Pasek postępu + pozostały czas. */}
      <div className={styles.progress}>
        {remain !== null && poll.fill_time > 0 && (
          <div className={styles.remainTime}>{t.poll.remainingTime(formatDuration(remain))}</div>
        )}
        {progress.question_count > 0 && (
          <div className={styles.remainQuestions}>
            {t.poll.remainingQuestions(remainingQuestions, percentDone)}
          </div>
        )}
      </div>

      {errors.submit && <FormStatus variant="error">{errors.submit}</FormStatus>}

      <div className={styles.pollActions}>
        <LinkButton href={backHref} fallback={content.list_href} variant="secondary">
          {t.poll.back}
        </LinkButton>
        <SubmitButton loading={submitting} loadingLabel={t.poll.submitting}>
          {isLastStep ? t.poll.end : t.poll.next}
        </SubmitButton>
      </div>
    </form>
  );
}

/* ─── Single question field (per typ) ───────────────────────────────────────── */

function QuestionField({
  question,
  number,
  value,
  justification,
  error,
  onChange,
  onJustificationChange,
  t,
}: {
  question: CPollQuestion;
  number: number;
  value: string | string[] | undefined;
  justification: string;
  error?: string;
  onChange: (value: string | string[]) => void;
  onJustificationChange: (text: string) => void;
  t: CPollsTranslations;
}) {
  const heading = (
    <div className={styles.questionHead}>
      <span className={styles.questionNumber}>{number}</span>
      <div className={styles.questionTitleWrap}>
        <h3 className={styles.questionTitle}>{question.name}</h3>
        {question.text && (
          <div
            className={styles.questionText}
            dangerouslySetInnerHTML={{ __html: question.text }}
          />
        )}
      </div>
    </div>
  );

  /* typ 1 - jednokrotny (radio) + opcjonalne uzasadnienie wybranej odpowiedzi. */
  if (question.typ === 1) {
    const selectedId = typeof value === "string" ? value : "";
    const selectedAnswer = question.answers.find((a) => String(a.id) === selectedId);
    const showJustification = !!selectedAnswer?.justification;
    return (
      <fieldset className={styles.answerSet}>
        <legend className={styles.srOnly}>{question.name}</legend>
        {heading}
        <div className={styles.choices}>
          {question.answers.map((a) => (
            <Radio
              key={a.id}
              name={`q-${question.id}`}
              value={String(a.id)}
              checked={selectedId === String(a.id)}
              onChange={() => onChange(String(a.id))}
            >
              {a.name}
            </Radio>
          ))}
        </div>
        {showJustification && (
          <Field label={t.poll.labels.justification} className={styles.justification}>
            <Textarea
              rows={4}
              value={justification}
              placeholder={selectedAnswer?.justification_placeholder || undefined}
              onChange={(e) => onJustificationChange(e.target.value)}
            />
          </Field>
        )}
        {error && <span className={styles.fieldError} role="alert">{error}</span>}
      </fieldset>
    );
  }

  /* typ 2 - wielokrotny (checkbox). */
  if (question.typ === 2) {
    const selected = Array.isArray(value) ? value : [];
    const toggle = (id: string, checked: boolean) => {
      onChange(checked ? [...selected, id] : selected.filter((v) => v !== id));
    };
    return (
      <fieldset className={styles.answerSet}>
        <legend className={styles.srOnly}>{question.name}</legend>
        {heading}
        <div className={styles.choices}>
          {question.answers.map((a) => (
            <Checkbox
              key={a.id}
              value={String(a.id)}
              checked={selected.includes(String(a.id))}
              onChange={(e) => toggle(String(a.id), e.target.checked)}
            >
              {a.name}
            </Checkbox>
          ))}
        </div>
        {error && <span className={styles.fieldError} role="alert">{error}</span>}
      </fieldset>
    );
  }

  /* typ 3 - krótki tekst. */
  if (question.typ === 3) {
    return (
      <div className={styles.answerSet}>
        {heading}
        <Field error={error}>
          <Input
            type="text"
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      </div>
    );
  }

  /* typ 4 - długi tekst (textarea). */
  if (question.typ === 4) {
    return (
      <div className={styles.answerSet}>
        {heading}
        <Field error={error}>
          <Textarea
            rows={5}
            value={typeof value === "string" ? value : ""}
            onChange={(e) => onChange(e.target.value)}
          />
        </Field>
      </div>
    );
  }

  /* typ 5 - ranking (sortowanie kolejności odpowiedzi). */
  return (
    <div className={styles.answerSet}>
      {heading}
      <RankingQuestion
        question={question}
        order={Array.isArray(value) ? value : question.answers.map((a) => String(a.id))}
        onChange={onChange}
        t={t}
      />
      {error && <span className={styles.fieldError} role="alert">{error}</span>}
    </div>
  );
}

/* ─── Ranking (typ 5) - drag & drop + przyciski strzałek (a11y) ──────────────── */

function RankingQuestion({
  question,
  order,
  onChange,
  t,
}: {
  question: CPollQuestion;
  order: string[];
  onChange: (order: string[]) => void;
  t: CPollsTranslations;
}) {
  const dragIndex = useRef<number | null>(null);

  const answerById = (id: string) => question.answers.find((a) => String(a.id) === id);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length) return;
    const next = [...order];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item);
    onChange(next);
  };

  return (
    <>
      <p className={styles.rankingHint}>{t.poll.rankingHint}</p>
      <ul className={styles.ranking}>
        {order.map((id, idx) => {
          const answer = answerById(id);
          if (!answer) return null;
          return (
            <li
              key={id}
              className={styles.rankingItem}
              draggable
              onDragStart={() => {
                dragIndex.current = idx;
              }}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => {
                if (dragIndex.current !== null && dragIndex.current !== idx) {
                  move(dragIndex.current, idx);
                }
                dragIndex.current = null;
              }}
            >
              <span className={styles.rankingOrder}>{idx + 1}</span>
              <span className={styles.rankingName}>{answer.name}</span>
              <span className={styles.rankingButtons}>
                <button
                  type="button"
                  className={styles.rankingBtn}
                  aria-label={t.poll.moveUp}
                  disabled={idx === 0}
                  onClick={() => move(idx, idx - 1)}
                >
                  <ChevronIcon direction="up" />
                </button>
                <button
                  type="button"
                  className={styles.rankingBtn}
                  aria-label={t.poll.moveDown}
                  disabled={idx === order.length - 1}
                  onClick={() => move(idx, idx + 1)}
                >
                  <ChevronIcon direction="down" />
                </button>
              </span>
            </li>
          );
        })}
      </ul>
    </>
  );
}

/* ─── Completed view ────────────────────────────────────────────────────────── */

function CompletedView({
  content,
  t,
}: {
  content: CPollsCompletedContent;
  t: CPollsTranslations;
}) {
  const scoreHref = content.score_href ? extractInternalHref(content.score_href) : null;
  const backHref = extractInternalHref(content.list_href);
  return (
    <div className={styles.completed}>
      <FormStatus variant="success">{t.completed.thankYou}</FormStatus>
      <div className={styles.pollActions}>
        {scoreHref !== null && (
          <LinkButton href={scoreHref} fallback={content.score_href!}>
            {t.completed.showResults}
          </LinkButton>
        )}
        <LinkButton href={backHref} fallback={content.list_href} variant="secondary">
          {t.completed.backToList}
        </LinkButton>
      </div>
    </div>
  );
}

/* ─── Score view (ECharts) ─────────────────────────────────────────────────── */

function ScoreView({
  content,
  t,
}: {
  content: CPollsScoreContent;
  t: CPollsTranslations;
}) {
  const backHref = extractInternalHref(content.list_href);
  const percentage = content.poll.show_score_in_percentage;
  const valueLabel = percentage ? t.score.percent : t.score.votes;
  const questions: CPollScoreQuestion[] = Array.isArray(content.questions) ? content.questions : [];

  return (
    <div className={styles.score}>
      {questions.length === 0 ? (
        <div className={styles.empty}>{t.score.noData}</div>
      ) : (
        questions.map((q) => (
          <section key={q.id} className={styles.scoreQuestion}>
            <h3 className={styles.scoreQuestionTitle}>{q.name}</h3>
            {q.text && (
              <div
                className={styles.scoreQuestionText}
                dangerouslySetInnerHTML={{ __html: q.text }}
              />
            )}
            {q.answers.length > 0 ? (
              <PollScoreChart answers={q.answers} valueLabel={valueLabel} percentage={percentage} />
            ) : (
              <div className={styles.empty}>{t.score.noData}</div>
            )}
          </section>
        ))
      )}
      <div className={styles.pollActions}>
        <LinkButton href={backHref} fallback={content.list_href} variant="secondary">
          {t.score.backToList}
        </LinkButton>
      </div>
    </div>
  );
}

/** Inline SVG chevron (zero dependency, ASCII source) dla przycisków rankingu. */
function ChevronIcon({ direction }: { direction: "up" | "down" }) {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={direction === "down" ? { transform: "rotate(180deg)" } : undefined}
    >
      <polyline points="6 15 12 9 18 15" />
    </svg>
  );
}

/* ─── Shared link button ────────────────────────────────────────────────────── */

function LinkButton({
  href,
  fallback,
  variant = "primary",
  children,
}: {
  href: string | null;
  fallback: string;
  variant?: "primary" | "secondary";
  children: React.ReactNode;
}) {
  const cls = `${styles.button} ${variant === "secondary" ? styles.buttonSecondary : ""}`.trim();
  if (href !== null) {
    return (
      <Link href={href} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <a href={fallback} className={cls}>
      {children}
    </a>
  );
}

/* ─── Helpers ───────────────────────────────────────────────────────────────── */

/** Czy pytanie ma już udzieloną odpowiedź (typ 5 ranking zawsze ma komplet). */
function isAnswered(q: CPollQuestion, value: string | string[] | undefined): boolean {
  if (q.typ === 5) return true;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "string") return value.trim() !== "";
  return false;
}

/** Inicjalizuje stan głosów per typ pytania. */
function buildInitialVotes(questions: CPollQuestion[]): VotesState {
  const out: VotesState = {};
  for (const q of questions) {
    if (q.typ === 2) out[q.id] = [];
    else if (q.typ === 5) out[q.id] = q.answers.map((a) => String(a.id));
    else out[q.id] = "";
  }
  return out;
}

/** Buduje payload głosów do API (konwersja typów per pytanie). */
function buildPayloadVotes(
  questions: CPollQuestion[],
  votes: VotesState,
): Record<string, CPollVoteValue> {
  const out: Record<string, CPollVoteValue> = {};
  for (const q of questions) {
    const v = votes[q.id];
    if (q.typ === 1) {
      out[q.id] = typeof v === "string" && v !== "" ? Number(v) : "";
    } else if (q.typ === 2 || q.typ === 5) {
      out[q.id] = Array.isArray(v) ? v.map((x) => Number(x)) : [];
    } else {
      out[q.id] = typeof v === "string" ? v : "";
    }
  }
  return out;
}

/** Buduje payload uzasadnień (tylko typ=1 z odpowiedzią justification). */
function buildPayloadJustifications(
  questions: CPollQuestion[],
  votes: VotesState,
  justifications: Record<number, string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const q of questions) {
    if (q.typ !== 1) continue;
    const selectedId = typeof votes[q.id] === "string" ? (votes[q.id] as string) : "";
    const answer = q.answers.find((a) => String(a.id) === selectedId);
    if (answer?.justification && justifications[q.id]) {
      out[q.id] = justifications[q.id];
    }
  }
  return out;
}

/** Walidacja formularza (wymagalność pól gościa + pytań). */
function validateForm(
  questions: CPollQuestion[],
  votes: VotesState,
  guestRequired: boolean,
  guestData: { email: string; name: string; surname: string; telephone: string },
  t: CPollsTranslations,
): ErrorsState {
  const errors: ErrorsState = {};

  if (guestRequired) {
    if (!guestData.email.trim()) errors.email = t.poll.errors.required;
    else if (!/.+@.+\..+/.test(guestData.email.trim())) errors.email = t.poll.errors.email;
    if (!guestData.name.trim()) errors.name = t.poll.errors.required;
    if (!guestData.surname.trim()) errors.surname = t.poll.errors.required;
    if (!guestData.telephone.trim()) errors.telephone = t.poll.errors.required;
  }

  const voteErrors: Record<number, string> = {};
  for (const q of questions) {
    if (!isAnswered(q, votes[q.id])) voteErrors[q.id] = t.poll.errors.questionRequired;
  }
  if (Object.keys(voteErrors).length > 0) errors.votes = voteErrors;

  return errors;
}

function hasErrors(errors: ErrorsState): boolean {
  return (
    !!errors.email ||
    !!errors.name ||
    !!errors.surname ||
    !!errors.telephone ||
    !!errors.submit ||
    (!!errors.votes && Object.keys(errors.votes).length > 0)
  );
}

/** Mapuje kody błędów z backendu na komunikaty i18n. */
function mapBackendErrors(
  backend: Extract<Awaited<ReturnType<typeof savePoll>>, { ok: false }>["errors"],
  message: string | undefined,
  t: CPollsTranslations,
): ErrorsState {
  const out: ErrorsState = {};
  if (!backend) {
    out.submit = message || t.poll.errors.submit;
    return out;
  }
  const codeToMsg = (code: string | undefined): string => {
    switch (code) {
      case "email":
        return t.poll.errors.email;
      case "taken":
        return t.poll.errors.emailTaken;
      case "required":
        return t.poll.errors.required;
      default:
        return t.poll.errors.required;
    }
  };
  if (backend.email) out.email = codeToMsg(backend.email);
  if (backend.name) out.name = t.poll.errors.required;
  if (backend.surname) out.surname = t.poll.errors.required;
  if (backend.telephone) out.telephone = t.poll.errors.required;
  if (backend.votes) {
    const v: Record<number, string> = {};
    for (const [qid] of Object.entries(backend.votes)) {
      v[Number(qid)] = t.poll.errors.questionRequired;
    }
    out.votes = v;
  }
  return out;
}

/** Sekundy -> "MM:SS". */
function formatDuration(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

